/* pdp-gallery.js — the shared image gallery engine, used by the product
   page AND news posts (one gallery, not two). Native scroll + CSS snap do
   the gesture work; this file adds the infinite loop, the step arrows, the
   draggable progress scrubber and the current-image callback. ONE code
   path at every width — vertical >990px, horizontal below; the phone
   presentation is pure CSS. Cells are sized by CSS container units, never
   by JS, so size changes only ever need a scroll correction.

   How the loop stays solid:
   - The strip holds THREE copies of the images and the user lives in the
     middle one. A full copy of margin each side means a missed rebase is
     harmless — the strip can't hit a hard edge mid-gesture, and because
     scroll-snap-stop:always settles after every swipe there's a rebase
     opportunity between any two gestures.
   - A rebase (teleport by one copy, snap suspended) happens ONLY when the
     scroller is verifiably still: same position across two timed checks,
     no finger down, no tween running. Never mid-momentum.
   - One scroll writer at a time: the arrow tween and the scrubber drag
     share an ownership token; taking it stops the other.

   API (set on the [data-pdp-gallery] element):
   - __pdpOnChange(cb): cb(realIndex, count) now + whenever it changes.
   - __pdpWhenIdle(cb): cb() once nothing scrolls/animates (zoom resize).
   - __pdpRelayout(): synchronous scroll correction after a size change.
   - __pdpDestroy(): detach window listeners (hosts that swap content).

   Call window.initPdpGallery(root) where `root` contains a .pdp__media
   with [data-pdp-gallery] / [data-pdp-scroller] / [data-pdp-track] /
   [data-pdp-progress]. */

window.initPdpGallery = function (root) {
  if (!root) return;

  var gallery = root.querySelector('[data-pdp-gallery]');
  var scroller = root.querySelector('[data-pdp-scroller]');
  var track = root.querySelector('[data-pdp-track]');
  if (!gallery || !scroller || !track) return;
  if (gallery.__pdpDestroy) gallery.__pdpDestroy(); /* re-init safe */

  var originals = Array.prototype.slice.call(track.querySelectorAll('.pdp__cell'));
  var n = originals.length;
  if (n < 1) return;
  var looping = n > 1;

  /* Three copies: [copy | ORIGINALS | copy]; `base` = the middle copy's
     first cell. The clones are presentation-only duplicates. */
  var base = 0;
  if (looping) {
    base = n;
    var before = document.createDocumentFragment();
    var after = document.createDocumentFragment();
    originals.forEach(function (c) {
      var a = c.cloneNode(true);
      a.setAttribute('aria-hidden', 'true');
      before.appendChild(a);
      var b = c.cloneNode(true);
      b.setAttribute('aria-hidden', 'true');
      after.appendChild(b);
    });
    track.insertBefore(before, originals[0]);
    track.appendChild(after);
  }
  var total = looping ? n * 3 : n;

  /* ---- Axis + position. The 990px layout breakpoint decides the axis;
     everything below reads these four, nothing caches a mode. ---- */
  var axisMql = window.matchMedia('(max-width: 990px)');
  function horiz() { return axisMql.matches; }
  function viewport() { return horiz() ? scroller.clientWidth : scroller.clientHeight; }
  function pos() { return horiz() ? scroller.scrollLeft : scroller.scrollTop; }
  function setPos(p) { if (horiz()) scroller.scrollLeft = p; else scroller.scrollTop = p; }

  function snapOff() { scroller.style.scrollSnapType = 'none'; }
  /* Restore two frames later — a single rAF can re-enable snap before the
     scroll write commits and yank the strip back where it came from. */
  function snapOnSoon() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { scroller.style.scrollSnapType = ''; });
    });
  }

  /* ---- Current image: ONE derivation, every consumer reads these. ---- */
  function cindex() { /* continuous image index, 0 = first image, unclamped */
    var v = viewport();
    return v ? pos() / v - base : 0;
  }
  function realIndex() { return ((Math.round(cindex()) % n) + n) % n; }

  var changeCbs = [];
  var lastEmitted = -1;
  function emit() {
    var r = realIndex();
    if (r === lastEmitted) return;
    lastEmitted = r;
    for (var i = 0; i < changeCbs.length; i++) changeCbs[i](r, n);
  }
  gallery.__pdpOnChange = function (cb) {
    changeCbs.push(cb);
    cb(realIndex(), n);
  };

  /* Progress line: three stacked thumb copies offset by ±n so the bar slides
     off one edge and back in the other at the loop. Position (--p) + size
     (--s) as %; CSS maps them to left/width (horizontal) or top/height
     (vertical). Driven 1:1 from the scroll (or the pointer while dragging). */
  var progressThumbs = Array.prototype.slice.call(root.querySelectorAll('[data-pdp-progress-thumb]'));
  function drawThumbs(p) { /* p = continuous real-image index */
    for (var i = 0; i < progressThumbs.length && i < 3; i++) {
      var off = (i - 1) * n; /* -n · 0 · +n */
      progressThumbs[i].style.setProperty('--p', ((p + off) / n * 100) + '%');
      progressThumbs[i].style.setProperty('--s', (100 / n) + '%');
    }
  }

  /* Reveal the progress bar while the gallery is actively scrolling (fades
     back out shortly after it settles); hover + drag are handled in CSS. */
  var mediaEl = root.querySelector('.pdp__media');
  var scrollingTimer;
  function markScrolling() {
    if (!mediaEl) return;
    mediaEl.classList.add('is-scrolling');
    clearTimeout(scrollingTimer);
    scrollingTimer = setTimeout(function () { mediaEl.classList.remove('is-scrolling'); }, 900);
  }

  /* ---- One scroll writer at a time: 'tween' (arrows / scrub release) or
     'drag' (scrubber). Taking ownership stops the other. Native gestures
     interrupt a tween via touchstart below. ---- */
  var owner = null;
  var tweenRAF = null, tweenTarget = null;
  function stopTween() {
    if (tweenRAF) cancelAnimationFrame(tweenRAF);
    tweenRAF = null;
    tweenTarget = null;
    if (owner === 'tween') { owner = null; scroller.style.scrollSnapType = ''; }
  }

  /* ---- Rebase: teleport by one copy back into the middle. The pixels are
     identical either side, so it's invisible — and it only ever runs from
     the settled state (see settleCheck), never mid-momentum. ---- */
  var rebasing = false;
  function rebase() {
    if (!looping) return;
    var v = viewport();
    if (!v) return;
    var d = Math.round(pos() / v);
    if (d >= base && d < base + n) return; /* already in the middle copy */
    rebasing = true;
    snapOff();
    setPos((base + ((d % n) + n) % n) * v);
    snapOnSoon();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { rebasing = false; });
    });
  }

  /* ---- Settled = same position across two timed checks with no finger
     down and no tween running. Position comparison (not scroll-event
     silence) — iOS fires scroll events sparsely during momentum, so event
     gaps lie; the position can't. ---- */
  var touching = false;
  var settleTimer = null;
  var settlePos = -1;
  var idleCbs = [];
  function isIdle() { return !owner && !touching && settleTimer === null; }
  function flushIdle() {
    while (idleCbs.length) idleCbs.shift()();
  }
  gallery.__pdpWhenIdle = function (cb) {
    if (isIdle()) cb(); else idleCbs.push(cb);
  };
  function settleCheck() {
    settleTimer = null;
    if (owner || touching) { settlePos = -1; return; } /* re-armed on release */
    var p = pos();
    if (p !== settlePos) {
      settlePos = p;
      settleTimer = setTimeout(settleCheck, 90);
      return;
    }
    rebase();
    emit();
    flushIdle();
  }
  function settleSoon() {
    settlePos = -1;
    clearTimeout(settleTimer);
    settleTimer = setTimeout(settleCheck, 90);
  }

  /* ---- Eased glide to cell d (ease-out-expo): launches near-instantly,
     settles slowly. Owns the scroll for its duration. ---- */
  function glideTo(d) {
    stopTween();
    var v = viewport();
    var start = pos();
    var dist = d * v - start;
    if (Math.abs(dist) < 1) {
      scroller.style.scrollSnapType = '';
      rebase();
      settleSoon();
      return;
    }
    owner = 'tween';
    tweenTarget = d;
    snapOff();
    var dur = 650;
    var t0 = null;
    function frame(ts) {
      if (t0 === null) t0 = ts;
      var p = Math.min(1, (ts - t0) / dur);
      var e = p === 1 ? 1 : 1 - Math.pow(2, -10 * p); /* ease-out-expo */
      setPos(start + dist * e);
      if (p < 1) { tweenRAF = requestAnimationFrame(frame); return; }
      tweenRAF = null;
      tweenTarget = null;
      owner = null;
      rebase(); /* step off a side copy before snap returns */
      snapOnSoon();
      settleSoon();
    }
    tweenRAF = requestAnimationFrame(frame);
  }

  /* Step arrows: one image per click; rapid clicks retarget from the pending
     destination. With a copy of margin each side, the clamp is never felt. */
  root.querySelectorAll('[data-pdp-step]').forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      e.stopPropagation();
      if (owner === 'drag') return; /* the finger owns the position */
      var v = viewport();
      if (!v) return;
      var dir = parseInt(btn.getAttribute('data-pdp-step'), 10) || 0;
      var from = tweenTarget !== null ? tweenTarget : Math.round(pos() / v);
      glideTo(Math.max(0, Math.min(from + dir, total - 1)));
    });
  });

  /* ---- Draggable / clickable progress scrubber. The thumb tracks the
     pointer 1:1; the strip glides toward it with a slight lag. On release,
     an eased glide snaps to the nearest image. ---- */
  var progressBar = root.querySelector('[data-pdp-progress]');
  if (progressBar && looping) {
    var barFrac = function (e) {
      var r = progressBar.getBoundingClientRect();
      var f = horiz() ? (e.clientX - r.left) / r.width : (e.clientY - r.top) / r.height;
      return Math.max(0, Math.min(1, f));
    };
    var barTarget = 0, barCur = 0, barRAF = null;
    var barLoop = function () {
      barRAF = null;
      barCur += (barTarget - barCur) * 0.16; /* ease factor → the slight lag */
      if (Math.abs(barTarget - barCur) < 0.5) barCur = barTarget;
      setPos(barCur);
      if (owner === 'drag') barRAF = requestAnimationFrame(barLoop);
    };
    var barSet = function (f) {
      var v = viewport();
      if (!v) return;
      var idx = f * n; /* continuous real index under the pointer */
      drawThumbs(idx); /* the thumb leads; the strip follows */
      barTarget = (base + idx) * v; /* always inside the strip (3 copies) */
      if (!barRAF && owner === 'drag') barRAF = requestAnimationFrame(barLoop);
    };
    progressBar.addEventListener('pointerdown', function (e) {
      var v = viewport();
      if (!v) return;
      stopTween();
      owner = 'drag';
      progressBar.classList.add('is-dragging');
      try { progressBar.setPointerCapture(e.pointerId); } catch (_) {}
      snapOff();
      barCur = pos();
      barSet(barFrac(e));
      e.preventDefault();
    });
    progressBar.addEventListener('pointermove', function (e) {
      if (owner === 'drag') barSet(barFrac(e));
    });
    var barEnd = function (e) {
      if (owner !== 'drag') return;
      owner = null;
      progressBar.classList.remove('is-dragging');
      try { progressBar.releasePointerCapture(e.pointerId); } catch (_) {}
      if (barRAF) { cancelAnimationFrame(barRAF); barRAF = null; }
      var v = viewport();
      if (!v) { scroller.style.scrollSnapType = ''; return; }
      glideTo(Math.round(pos() / v)); /* eased settle onto the nearest cell */
    };
    progressBar.addEventListener('pointerup', barEnd);
    progressBar.addEventListener('pointercancel', barEnd);
  }

  /* ---- Scroll wiring. Programmatic moves (rebase / layout) don't count as
     user scrolls, so they never fade the arrows / hint. ---- */
  var layouting = false;
  scroller.addEventListener('scroll', function () {
    if (!rebasing && !layouting) {
      gallery.classList.add('has-scrolled');
      markScrolling();
    }
    if (owner !== 'drag') drawThumbs(cindex()); /* dragging: pointer-driven */
    emit();
    settleSoon();
  }, { passive: true });
  if ('onscrollend' in scroller) scroller.addEventListener('scrollend', settleSoon);

  /* A new touch takes over cleanly: stop any tween, and hold off settling
     (and thus rebasing) until the finger lifts. */
  scroller.addEventListener('touchstart', function () {
    touching = true;
    stopTween();
  }, { passive: true });
  var onTouchEnd = function () {
    touching = false;
    settleSoon();
  };
  scroller.addEventListener('touchend', onTouchEnd, { passive: true });
  scroller.addEventListener('touchcancel', onTouchEnd, { passive: true });

  /* ---- Layout. Cells are CSS-sized (container units), so JS only ever
     corrects the scroll offset. ---- */
  var lastV = 0;
  var lastHoriz = horiz();
  function jumpToIndex(idx) {
    layouting = true;
    snapOff();
    setPos((base + idx) * viewport());
    snapOnSoon();
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { layouting = false; });
    });
    drawThumbs(cindex());
    emit();
    settleSoon();
  }
  /* Scroll correction after the gallery box changes (resize, axis flip, the
     news zoom): keep the SAME image. The index comes from lastEmitted — the
     engine's live notion of the nearest image — NOT from re-deriving against
     a stored viewport: by relayout time the box has already changed and the
     browser's scroll anchoring may have rewritten the offset, so arithmetic
     on old numbers lands one image off. lastEmitted is always computed
     against the live viewport, so it survives both. */
  function relayout() {
    var v = viewport();
    if (!v) return;
    if (horiz() === lastHoriz && v === lastV) return; /* URL-bar height churn */
    lastV = v;
    lastHoriz = horiz();
    stopTween();
    jumpToIndex(lastEmitted >= 0 ? lastEmitted : 0);
  }
  gallery.__pdpRelayout = relayout;

  var resizeTimer;
  var onResize = function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(relayout, 120);
  };
  window.addEventListener('resize', onResize);
  var onAxis = function () { relayout(); };
  if (axisMql.addEventListener) axisMql.addEventListener('change', onAxis);
  else if (axisMql.addListener) axisMql.addListener(onAxis);
  window.addEventListener('load', relayout);

  gallery.__pdpDestroy = function () {
    window.removeEventListener('resize', onResize);
    window.removeEventListener('load', relayout);
    if (axisMql.removeEventListener) axisMql.removeEventListener('change', onAxis);
    else if (axisMql.removeListener) axisMql.removeListener(onAxis);
    clearTimeout(settleTimer);
    clearTimeout(resizeTimer);
    clearTimeout(scrollingTimer);
    stopTween();
    gallery.__pdpDestroy = null;
  };

  /* Land on the first image synchronously (no flash of the leading copy),
     then correct once styles/images settle and again on full load. */
  lastV = viewport();
  jumpToIndex(0);
  requestAnimationFrame(relayout);
};
